<template>
  <modal
    :show="isVisible"
    :title="t('Re-exigir taxa de implantação')"
    elem-id="modal_setup_fee_rerequire"
    size="compactDesktop"
    :close-label="t('Cancelar')"
    :save-label="t('Re-exigir taxa')"
    :save-fn="handleSave"
    :save-disabled-fn="() => isSaving || !franquiaId || !reason.trim()"
    save-test-id="setup-fee-rerequire-submit"
    @close="handleClose"
    @dismiss="handleClose"
  >
    <template v-if="isVisible">
      <div class="rerequire-fields">
        <franchise-select
          v-if="!hasFixedFranchise"
          v-model="franquiaId"
          elem-id="setup-fee-rerequire-franchise"
          :label="t('Franquia')"
          data-testid="setup-fee-rerequire-franchise-select"
        />
        <div v-else class="franchise-info">
          <label class="info-label">{{ t('Franquia') }}:</label>
          <span class="info-value" data-testid="setup-fee-rerequire-franchise-name">{{ franchiseName || franquiaId }}</span>
        </div>

        <input-text
          v-model="reason"
          elem-id="setup-fee-rerequire-reason"
          :label="t('Motivo da re-exigência')"
          :ng-required="true"
          data-testid="setup-fee-rerequire-reason-input"
        />

        <div v-if="errorMessage" class="error-message" data-testid="setup-fee-rerequire-error">
          {{ errorMessage }}
        </div>
      </div>
    </template>
  </modal>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount } from 'vue'
import { useI18n } from 'vue-i18n'
import { eventBus } from '../../../eventBus'
import { useSetupFee } from '../../../composables/useSetupFee'
import { log } from '../../../utils/DebugLogger'
import Modal from '../../../ui/Modal.vue'
import FranchiseSelect from '../../../inputs/FranchiseSelect.vue'
import InputText from '../../../inputs/InputText.vue'

const { t } = useI18n()
const { rerequire } = useSetupFee()

const isVisible = ref(false)
const isSaving = ref(false)
const hasFixedFranchise = ref(false)
const franquiaId = ref<number | string | null>(null)
const franchiseName = ref<string>('')
const reason = ref<string>('')
const errorMessage = ref<string>('')

function handleOpen(payload?: { franquiaId?: number; franchiseName?: string }): void {
  log('SetupFeeRerequireModal', 'handleOpen', 'Opening rerequire modal', payload)
  franquiaId.value = payload?.franquiaId ?? null
  franchiseName.value = payload?.franchiseName ?? ''
  hasFixedFranchise.value = Boolean(payload?.franquiaId)
  reason.value = ''
  errorMessage.value = ''
  isVisible.value = true
}

function handleClose(): void {
  log('SetupFeeRerequireModal', 'handleClose', 'Closing rerequire modal')
  isVisible.value = false
}

async function handleSave(): Promise<void> {
  if (!franquiaId.value || !reason.value.trim()) return
  isSaving.value = true
  errorMessage.value = ''

  try {
    const result = await rerequire({
      franquia_id: Number(franquiaId.value),
      reason: reason.value.trim(),
    })
    log('SetupFeeRerequireModal', 'handleSave', 'Rerequire succeeded', result)
    eventBus.emit('setup-fee.obligation-updated', result)
    eventBus.emit('subscriptions.refresh')
    eventBus.emit('globalMessage.success', t('Taxa de implantação re-exigida com sucesso'))
    handleClose()
  } catch (err) {
    errorMessage.value = (err as Error)?.message || t('Erro ao re-exigir taxa de implantação')
    log('SetupFeeRerequireModal', 'handleSave', 'Rerequire error', { error: (err as Error)?.message })
  } finally {
    isSaving.value = false
  }
}

onMounted(() => {
  eventBus.on('modal.setup-fee-rerequire.open', handleOpen)
})

onBeforeUnmount(() => {
  eventBus.off('modal.setup-fee-rerequire.open', handleOpen)
})
</script>

<style scoped>
.rerequire-fields {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
.franchise-info {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}
.info-label {
  font-size: 0.875rem;
  font-weight: 600;
  color: #4b5563;
}
.info-value {
  font-size: 1rem;
  font-weight: 500;
  color: #111827;
}
.error-message {
  color: #dc3545;
  font-size: 0.875rem;
}
</style>
